import React, { useState, useCallback } from "react";
import { Status } from "../assets/data/Constants";

/**
 * 複数の配列を結合するユーティリティ関数
 * @param arrays 結合する配列
 * @returns 結合された配列
 * @template T
 */
const useConcatArrays = <T>(...arrays: T[][]): T[] =>
  arrays.reduce((acc, cur) => acc.concat(cur), []);

/**
 * スロットルするカスタムフック
 * @param delay スロットルする時間（ミリ秒）
 * @param fun 実行したい関数
 * @returns スロットルされたイベントハンドラー
 */
const useThrottled = (
  delay: number,
  fun: any
): React.EventHandler<React.UIEvent> => {
  const [lastScrollTime, setLastScrollTime] = useState(0);

  const handleScroll = useCallback(
    (_: React.UIEvent) => {
      const now = Date.now();
      if (now - lastScrollTime >= delay) {
        setLastScrollTime(now);
        // イベントハンドラーの処理
        fun && fun();
      }
    },
    [delay, lastScrollTime, fun]
  );

  return handleScroll;
};

/**
 * イベントをデバウンスするカスタムフック
 * @param delay デバウンスする時間（ミリ秒）
 * @returns デバウンスされたイベントハンドラー
 */
const useDebouncedEvent = <T extends (...args: any[]) => void>(
  eventHandler: T,
  delay: number
): T => {
  const [timerId, setTimerId] = useState<NodeJS.Timeout | null>(null);

  const debouncedEventHandler = useCallback(
    (...args: any[]) => {
      if (timerId) {
        clearTimeout(timerId);
      }
      const newTimerId = setTimeout(() => {
        eventHandler(...args);
      }, delay);
      setTimerId(newTimerId);
    },
    [eventHandler, delay, timerId]
  );

  return debouncedEventHandler as T;
};

/**
 * システム日付を取得するカスタムフック
 * @returns システム日付
 */
const useGetNowDate = () => {
  if (process.env.NODE_ENV === "development") {
    const parts = process.env.REACT_APP_DATE_OBJECT!.split("-");

    const dateObject = {
      year: parseInt(parts[0], 10),
      month: parseInt(parts[1], 10),
      date: parseInt(parts[2], 10),
      hour: parseInt(parts[3], 10),
      minute: parseInt(parts[4], 10),
      second: parseInt(parts[5], 10),
    };

    return dateObject;
  } else {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    const date = now.getDate();
    const hour = now.getHours();
    const minute = now.getMinutes();
    const second = now.getSeconds();

    return { year, month, date, hour, minute, second };
  }
};

/**
 * 3桁毎に,をつける部品
 * @param amount 元の数字又は文字列
 * @returns 3桁毎に,が入った文字列
 */
const changeAmountFormat = (amount: string | number): string => {
  if (typeof amount !== "string" && typeof amount !== "number") {
    return "";
  }
  if (isNaN(Number(amount))) {
    return "" + amount;
  }

  return Number(amount).toLocaleString();
};

/**
 * 俸給の調整額部品に使用するselList作成Hooks
 * @param AllowanceType 手当種別
 * "1": "特殊業務手当","2": "給与特別改善手当","3": "その他"
 * @param applyAllowanceNameList: APIからレスポンスで受け取る申請手当名リスト
 * @returns propsに必要なselList
 */

const createSelList = (AllowanceType: any, applyAllowanceNameList: any) => {
  let valueCounter = 2;
  return applyAllowanceNameList?.map((item: any) => {
    let classifyValue;
    for (const key in AllowanceType) {
      if (AllowanceType[key] === item.applyAllowanceName) {
        classifyValue = key;
        break;
      }
    }

    return {
      classfiy: classifyValue,
      label: item.applyAllowanceName,
      value: String(valueCounter++),
    };
  });
};

/**
 * 日付変換Hooks
 *
 * 認証情報.契約年月日を受け取って変換して返却します。
 * @param dateString : 認証情報.契約年月日
 * @return new Date(year, month, day)ornull
 **/
const parseDate = (dateString: string | undefined) => {
  //datastring = 認証情報.契約年月日
  if (!dateString) {
    return null;
  }
  const year = Number(dateString.slice(0, 4));
  const month = Number(dateString.slice(4, 6)) - 1;
  const day = Number(dateString.slice(6, 8));
  //datacheck
  if (isNaN(year) || isNaN(month) || isNaN(day)) {
    return null;
  }
  return new Date(year, month, day);
};

/**
 * 参照型の深い比較を行う関数
 * @param a 比較する参照型
 * @param b 比較する参照型
 * @returns オブジェクトが等しい場合はtrue、そうでない場合はfalse
 * @template T
 */
const deepCompare = (a: any, b: any) => {
  if (a === b) return true;

  if (a == null || typeof a != "object" || b == null || typeof b != "object") {
    return false;
  }

  const keysA = Object.keys(a);
  const keysB = Object.keys(b);

  if (keysA.length !== keysB.length) return false;

  for (const key of keysA) {
    if (!keysB.includes(key)) return false;

    if (!deepCompare(a[key], b[key])) return false;
  }

  return true;
};

/**
 * 郵便番号をハイフン付きのフォーマットに変換する
 * @param zipcode 郵便番号
 * @returns 変換後郵便番号
 */
const changeZipcodeFormat = (zipcode?: string): string => {
  if (zipcode) {
    return zipcode?.slice(0, 3) + "-" + zipcode?.slice(3);
  } else {
    return "";
  }
};

/**
 * 現在のデバイスがモバイルデバイスかどうかを判断する
 * @returns {boolean} モバイルデバイスの場合は true、それ以外の場合は false を返す
 */
const isMobileDevice = () => {
  if (typeof navigator === "undefined") {
    // サーバーサイドレンダリング（SSR）の場合、navigator は使用できない
    return false;
  }

  const userAgent = navigator.userAgent;

  // 一般的なモバイルデバイスの User-Agent キーワード
  const mobileRegex =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;

  return mobileRegex.test(userAgent);
};

/**
 * 最新申請状態が稟議・決裁結果待ち以降のステータスかを判断する
 * @param status 最新申請状態
 * @returns 稟議・決裁結果待ち以降の場合はtrue、それ以外はfalse
 */
const isStatusAfterApproval = (status: any): boolean => {
  const statusList = [
    Status["課長代理確認待ち"],
    Status["上長承認待ち"],
    Status["通知待ち"],
    Status["郵送待ち"],
    Status["処理完了"],
  ];

  return statusList.includes(status);
};

/**
 * 最新申請状態が受理待ち以降のステータスかを判断する
 * @param status 最新申請状態
 * @returns 受理待ち以降の場合はtrue、それ以外はfalse
 */
const isStatusAfterAwaitingReceipt = (status: any): boolean => {
  const statusList = [
    Status["再受理待ち"],
    Status["稟議決裁結果待ち"],
    Status["課長代理確認待ち"],
    Status["上長承認待ち"],
    Status["通知待ち"],
    Status["郵送待ち"],
    Status["処理完了"],
  ];

  return statusList.includes(status);
};

/**
 * 数字へ変換する関数
 * @param value
 * @returns
 */
const toNumberOrUndefined = (value: any) =>
  isNaN(Number(value)) ? undefined : Number(value);

// 年度取得
const getFiscalYear = (retireDate: any) => {
  const date = new Date(retireDate);
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  if (month >= 4) {
    return year;
  } else {
    return year - 1;
  }
};

export {
  useConcatArrays,
  useThrottled,
  useDebouncedEvent,
  useGetNowDate,
  changeAmountFormat,
  deepCompare,
  parseDate,
  createSelList,
  changeZipcodeFormat,
  isMobileDevice,
  isStatusAfterApproval,
  toNumberOrUndefined,
  isStatusAfterAwaitingReceipt,
  getFiscalYear,
};
